import * as Sentry from '@sentry/react';
import { DotsVertical } from '@untitledui/icons';
import { useState } from 'react';
import { IconButton } from '../../../components/buttons/IconButton';
import AboutDialog from '../../../components/dialogs/AboutDialog';
import ConfirmSignOutDialog from '../../../components/dialogs/ConfirmSignOutDialog';
import TrainingControlsDialog from '../../../components/dialogs/TrainingControlsDialog';
import DropDownMenu from '../../../components/general/DropDownMenu';
import MenuDivider from '../../../components/general/MenuDivider';
import MenuItemTile from '../../../components/general/MenuItemTile';
import { en } from '../../../locales/en';
import { useDialogQueueStore } from '../../../store/useDialogQueueStore';
import { useFollowedVehicleStore } from '../../../store/useFollowedVehicleStore';
import { useGcsStore } from '../../../store/useGcsStore';
import { useNotificationStore } from '../../../store/useNotificationStore';
import { useSelectedGcsConfigsStore } from '../../../store/useSelectedGcsConfigsStore';
import { AppError, ErrorCode, ErrorDomain } from '../../../types/errors/errors';
import { DEFAULT_NOTIFICATION_DURATION_MS, InAppNotificationType } from '../../../types/ui/inAppNotification';
import { getLogEntries } from '../../../utils/logBuffer';
import AppLogo from './AppLogo';
import CurrentUserTile from './CurrentUserTile';

const STATE_SNAPSHOT_FILENAME_PREFIX = 'roc-state-snapshot';
const LOG_BUFFER_FILENAME_PREFIX = 'roc-logs';

function SidePanelHeaderTile() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { showDialog, hideDialog } = useDialogQueueStore();
  const firstGcsId = useSelectedGcsConfigsStore((state) => state.selectedConfigs[0]?.id);
  const showNotification = useNotificationStore((state) => state.showNotification);
  const enableAutoFollow = useFollowedVehicleStore((state) => state.enableAutoFollow);

  const onSendLogsClicked = () => {
    try {
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');

      // File 1: GCS state snapshot
      const snapshotLines: string[] = [];

      Sentry.withScope((scope) => {
        useGcsStore.getState().data.forEach((gcsData, gcsId) => {
          const debugPayload = JSON.stringify(gcsData.toDebugPayload(), null, 2);
          scope.setExtra(gcsId, debugPayload);
          snapshotLines.push(`[${gcsId}]`);
          snapshotLines.push(debugPayload);
          snapshotLines.push('');
        });
        Sentry.captureException(new AppError({ domain: ErrorDomain.HTTP, code: ErrorCode.SEND_LOGS }));
      });

      const snapshotBlob = new Blob([snapshotLines.join('\n')], { type: 'text/plain;charset=utf-8' });
      const snapshotUrl = URL.createObjectURL(snapshotBlob);
      const snapshotAnchor = document.createElement('a');
      snapshotAnchor.href = snapshotUrl;
      snapshotAnchor.download = `${STATE_SNAPSHOT_FILENAME_PREFIX}-${timestamp}.txt`;
      document.body.appendChild(snapshotAnchor);
      snapshotAnchor.click();
      document.body.removeChild(snapshotAnchor);
      URL.revokeObjectURL(snapshotUrl);

      // File 2: log buffer
      const logEntries = getLogEntries();
      if (logEntries.length > 0) {
        const logLines = logEntries.map(
          (e) => `${new Date(e.ts).toISOString()} [${e.level.toUpperCase().padEnd(5)}] [${e.module}] ${e.message}`
        );
        const logBlob = new Blob([logLines.join('\n')], { type: 'text/plain;charset=utf-8' });
        const logUrl = URL.createObjectURL(logBlob);
        const logAnchor = document.createElement('a');
        logAnchor.href = logUrl;
        logAnchor.download = `${LOG_BUFFER_FILENAME_PREFIX}-${timestamp}.txt`;
        document.body.appendChild(logAnchor);
        logAnchor.click();
        document.body.removeChild(logAnchor);
        URL.revokeObjectURL(logUrl);
      }

      showNotification({
        type: InAppNotificationType.SUCCESS,
        body: en.inAppNotifications.sendLogsSuccessBody,
        durationMs: DEFAULT_NOTIFICATION_DURATION_MS
      });
    } catch {
      showNotification({
        type: InAppNotificationType.ERROR,
        body: en.inAppNotifications.sendLogsErrorBody,
        durationMs: DEFAULT_NOTIFICATION_DURATION_MS
      });
    }
    setIsDropdownOpen(false);
  };

  const onTrainingControlsClicked = () => {
    showDialog({
      id: 'training-controls-dialog',
      component: <TrainingControlsDialog onClose={hideDialog} />
    });
    setIsDropdownOpen(false);
  };

  const onAboutClicked = () => {
    showDialog({
      id: 'about-dialog',
      component: <AboutDialog onClose={hideDialog} />
    });
    setIsDropdownOpen(false);
  };

  const onSignOutClicked = () => {
    if (!firstGcsId) return;
    showDialog({
      id: 'confirm-sign-out-dialog',
      component: <ConfirmSignOutDialog gcsId={firstGcsId} onClose={hideDialog} />
    });
    setIsDropdownOpen(false);
  };

  return (
    <div style={{ position: 'relative', overflow: 'visible' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          gap: '12px',
          padding: '0px 8px'
        }}
      >
        {/* Centered Logo */}
        <AppLogo onActivated={enableAutoFollow} />

        {/* Overflow Menu Button */}
        <div
          style={{
            position: 'absolute',
            right: '0'
          }}
        >
          <IconButton
            icon={DotsVertical}
            onClick={() => setIsDropdownOpen((prev) => !prev)}
            title={en.sidePanelMenuItems.moreOptions}
          />
        </div>
      </div>

      <DropDownMenu isOpen={isDropdownOpen} onClose={() => setIsDropdownOpen(false)} top='50px' right='8px'>
        {firstGcsId && <CurrentUserTile gcsId={firstGcsId} />}
        <MenuDivider />
        <MenuItemTile onClick={onSendLogsClicked} label={en.sidePanelMenuItems.sendLogs} />
        <MenuItemTile onClick={onTrainingControlsClicked} label={en.sidePanelMenuItems.trainingControls} />
        <MenuDivider />
        <MenuItemTile onClick={onAboutClicked} label={en.sidePanelMenuItems.about} />
        <MenuDivider />
        <MenuItemTile
          onClick={onSignOutClicked}
          label={en.sidePanelMenuItems.signOut}
          textColor='var(--color-danger)'
          hoverBackgroundColor='var(--color-danger-hover)'
        />
      </DropDownMenu>
    </div>
  );
}

export default SidePanelHeaderTile;
